"use client";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { useSearchParams } from "next/navigation";

import FormLabel from "@/components/form/form-label";
import MultiSelect from "@/components/form/multi-select";
import Pagination from "@/components/form/pagination";
import RCRange from "@/components/form/rc-slider-range";
import { RowsDropdown } from "@/components/form/rows";
import FetchError from "@/components/ui/fetch-error";
import Loading from "@/components/ui/loading";

import QuestionnaireSearchTable from "./questionnaire-search-table";

import api from "@/lib/api";

type FormValues = {
  keyword?: string;
  study?: number[];
  survey?: number[];
  years?: number[];
  module?: number[];
  page_size?: number;
  page?: number;
};

type SurveyQuestionnaireSearchDataProps = {
  count: number;
  next: string;
  previous: string;
  results: {
    id: number;
    title: string;
    description: string;
    survey_title: string;
    module_title: string;
  }[];
};

type SurveyQuestionnaireSearchProps = {
  studies: { value: number; label: string }[];
  years: number[];
  survey_types: { value: number; label: string }[];
  surveys: { value: number; label: string }[];
  modules: { value: number; label: string }[];
};

export async function fetchSurveyDropdown(id: number) {
  const res = await api.get(`/srv/search/survey-dropdown/${id}`);
  return res;
}

export async function fetchModuleDropdown(id: number) {
  const res = await api.get(`/srv/search/module-dropdown/${id}`);
  return res;
}

export async function getQuestionnaireSearchData(
  page: string,
  page_size: string,
  study: number[],
  keyword: string,
  survey: number[],
  survey_type: number[],
  module: number[],
  yearStart: string,
  yearEnd: string,
) {
  const params = new URLSearchParams();

  try {
    params.append("keyword", keyword);
    survey.forEach((s) => params.append("surveys", s.toString()));
    study.forEach((s) => params.append("studies", s.toString()));
    survey_type.forEach((s) => params.append("survey_types", s.toString()));
    module.forEach((m) => params.append("modules", m.toString()));
    params.append("years", `${yearStart},${yearEnd}`);
    params.append("page", page);
    params.append("page_size", page_size);

    const response = await api.get<SurveyQuestionnaireSearchDataProps>(`/srv/search/data?${params.toString()}`);
    return response;
  } catch (error) {
    console.error("Error fetching publication data:", error);
    throw new Error("Failed to fetch publication data");
  }
}

export default function QuestionnaireSearchHome() {
  const [tableData, setTableData] = useState<SurveyQuestionnaireSearchDataProps>();
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [yearSelection, setYearSelection] = useState<number[]>();

  const searchParams = useSearchParams();
  const initialKeyword = searchParams.get("keyword") || " ";

  const { handleSubmit, control, watch, reset, setValue, register } = useForm<FormValues>({
    defaultValues: {
      survey: [],
      module: [],
      years: [],
      keyword: initialKeyword,
      study: [],
    },
  });

  const selectedSurvey = watch("survey");

  const [studies, setStudies] = useState<{ value: number; label: string }[]>([]);
  const [surveyType, setSurveyType] = useState<{ value: number; label: string }[]>([]);
  const [survey, setSurvey] = useState<{ value: number; label: string }[]>([]);
  const [module, setModule] = useState<{ value: number; label: string }[]>([]);

  // Fetch Home Data
  const { data, error, isPending } = useQuery({
    queryKey: ["srv-hcap-questionnaire-search"],
    queryFn: () => api.get<SurveyQuestionnaireSearchProps>(`/srv/search/home`),
  });

  useEffect(() => {
    if (data) {
      const hcapSurveyType = data.survey_types.find((s) => s.label === "HCAP");

      setStudies(data.studies);
      setSurveyType(hcapSurveyType ? [hcapSurveyType] : []);
      setSurvey(data.surveys);
      setModule(data.modules);
      setYearSelection(data.years);

      reset({
        study: [],
        survey: [],
        module: [],
        years: data.years,
        keyword: initialKeyword,
      });

      if (hcapSurveyType) {
        onSubmit(
          {
            page: 1,
            page_size: 10,
            keyword: "",
            study: [],
            years: data.years,
            survey: [],
            module: [],
          },
          [hcapSurveyType.value],
        );
      }
    }
  }, [data, reset, initialKeyword]);

  const { data: surveyOptions, error: surveyError } = useQuery({
    queryKey: ["questionnaire-search-survey", surveyType],
    queryFn: async (): Promise<{ value: number; label: string }[]> => {
      if (!Array.isArray(surveyType) || surveyType.length === 0) return data?.surveys || [];
      const responses = await Promise.all(surveyType.map((s) => fetchSurveyDropdown(s.value)));
      return responses.flat() as { value: number; label: string }[];
    },
    enabled: Array.isArray(surveyType) && surveyType.length > 0,
  });

  const { data: moduleOptions, error: moduleError } = useQuery({
    queryKey: ["modules", selectedSurvey],
    queryFn: async (): Promise<{ value: number; label: string }[]> => {
      if (!Array.isArray(selectedSurvey) || selectedSurvey.length === 0) return [];
      const responses = await Promise.all(selectedSurvey.map(fetchModuleDropdown));
      return responses.flat() as { value: number; label: string }[];
    },
    enabled: Array.isArray(selectedSurvey) && selectedSurvey.length > 0,
  });

  const onSubmit = async (formData: FormValues, surveyTypeOverride?: number[]) => {
    setLoading(true);
    try {
      const yearStart = yearSelection?.[0] ?? data?.years?.[0];
      const yearEnd = yearSelection?.[1] ?? data?.years?.[1];

      const finalSurveyType = surveyTypeOverride ?? surveyType.map((s) => s.value);

      if (yearStart !== undefined && yearEnd !== undefined) {
        const response = await getQuestionnaireSearchData(
          formData.page?.toString() || "1",
          formData.page_size?.toString() || "10",
          formData.study || [],
          formData.keyword || "",
          formData.survey || [],
          finalSurveyType,
          formData.module || [],
          yearStart.toString(),
          yearEnd.toString(),
        );
        setTableData(response);
      } else {
        console.error("Both years are required.");
      }
    } catch (error) {
      console.error("Error fetching questionnaire data:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    const formData = watch();
    if (formData?.years?.length === 2 && surveyType.length > 0) {
      onSubmit({
        ...formData,
        page: currentPage,
        page_size: formData.page_size || 10,
      });
    }
  }, [currentPage]);

  const resetForm = () => {
    if (data) {
      reset({
        study: [],
        survey: [],
        module: [],
        keyword: "",
        page_size: 10,
      });
      setCurrentPage(1);
      setYearSelection(data.years);
      const hcapSurveyType = data.survey_types.find((s) => s.label === "HCAP");
      if (hcapSurveyType) {
        setSurveyType([hcapSurveyType]);
        onSubmit(
          {
            page: 1,
            page_size: 10,
            keyword: "",
            study: [],
            years: [data.years[0], data.years[1]],
            survey: [],
            module: [],
          },
          [hcapSurveyType.value],
        );
      }
    }
  };

  if (isPending) return <Loading />;
  if (error || surveyError || moduleError) {
    const errorMessage = error?.message || surveyError?.message || moduleError?.message || "An unknown error occurred";
    return <FetchError message={errorMessage} />;
  }

  return (
    <>
      <div className="alert alert-primary mt-4">Many components are not searchable due to copyright issues.</div>
      <form onSubmit={handleSubmit((data) => onSubmit(data))} className="row g-3 mb-4">
        <div className="col-md-4 col-sm-6 mt-2">
          <FormLabel label="Keyword" />
          <input
            type="text"
            className="form-control"
            id="keyword"
            aria-describedby="keyword"
            placeholder="Keyword"
            {...register("keyword")}
          />
        </div>
        <div className="col-md-4 col-sm-6 mt-2">
          <MultiSelect options={studies} control={control} isPending={isPending} name="study" label="Study" />
        </div>
        <div className="col-md-4 col-sm-6 mt-2">
          <RCRange
            label="Year"
            min={data.years[0]}
            max={data.years[1]}
            value={Array.isArray(yearSelection) ? yearSelection : []}
            onChange={(value) => setYearSelection(Array.isArray(value) ? value : [value])}
          />
        </div>
        <div className="col-md-4 col-sm-6 mt-2">
          <MultiSelect
            options={surveyOptions || survey}
            control={control}
            isPending={isPending}
            name="survey"
            label="Survey"
          />
        </div>
        <div className="col-md-4 col-sm-6 mt-2">
          <MultiSelect
            options={moduleOptions || module}
            isPending={isPending}
            control={control}
            name="module"
            label="Module"
          />
        </div>
        <div className="col-12 d-flex flex-wrap justify-content-end align-items-end gap-2 mt-3">
          <div className="me-auto">
            <RowsDropdown name="page_size" control={control} onSubmit={handleSubmit((data) => onSubmit(data))} />
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary"
            id="reset-btn"
            title="Clear All"
            onClick={resetForm}
          >
            <span>RESET</span>
          </button>
          <button type="submit" className="btn g2-btn-blue" id="search-btn" title="Search">
            <span>SEARCH</span>
          </button>
        </div>
      </form>
      {loading && tableData == undefined ? <Loading /> : <QuestionnaireSearchTable data={tableData} />}
      {tableData && tableData.count > 0 && (
        <Pagination
          totalCount={tableData.count}
          page_size={10}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          previous={tableData.previous}
          next={tableData.next}
        />
      )}
    </>
  );
}
